// src/features/procedures/pages/EditProcedurePage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Alert,
  IconButton,
  CircularProgress,
  TextField,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
// Hooks
import { useProcedures } from '../hooks/useProcedures';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useNavigationStack } from '../../../context/NavigationStackContext';

// Components
import ProcedureStepsEditor from '../components/ProcedureStepsEditor';
import { FormPage, FormSection, FormActions } from '../../../components/common/form';

export default function EditProcedurePage() {
  const { t } = useTranslation(['procedures', 'common']);
  const navigate = useNavigate();
  const { procedureId } = useParams();
  const { showSuccess, showError } = useSnackbar();
  const { navigateBack } = useNavigationStack();

  // State
  const [procedure, setProcedure] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
  });
  const [steps, setSteps] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedStepId, setExpandedStepId] = useState(null);

  // API hooks
  const {
    fetchProcedureById,
    updateProcedure,
    addProcedureStep,
    loading: updating,
    error,
    clearError,
  } = useProcedures();

  // Load procedure data
  useEffect(() => {
    const loadProcedureData = async () => {
      if (!procedureId) return;

      try {
        setLoading(true);
        const result = await fetchProcedureById(procedureId);
        
        if (result.success) {
          const procedureData = result.data.data;
          setProcedure(procedureData);
          setFormData({
            name: procedureData.name || '',
            description: procedureData.description || '',
            is_active: procedureData.is_active !== false,
          });
          setSteps((procedureData.steps || []).sort((a, b) => a.step_order - b.step_order));
        } else {
          showError(t('procedures:errors.load_failed'));
        }
      } catch (error) {
        showError(t('procedures:errors.load_failed'));
      } finally {
        setLoading(false);
      }
    };

    loadProcedureData();
  }, [procedureId, fetchProcedureById, showError, t]);

  // Form handling
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear validation error for this field
    setValidationErrors(prev => {
      if (prev[field]) {
        const { [field]: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t('procedures:validation.name_required');
    } else if (formData.name.length < 2) {
      errors.name = t('procedures:validation.name_min_length');
    } else if (formData.name.length > 255) {
      errors.name = t('procedures:validation.name_max_length');
    }

    if (formData.description && formData.description.length > 2000) {
      errors.description = t('procedures:validation.description_max_length');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (!validateForm() || !procedure) {
      return;
    }

    try {
      const updateData = {
        name: formData.name.trim(),
        description: formData.description?.trim() || null,
        is_active: formData.is_active,
        steps: steps.map(step => ({
          step_id: !step.step_id.startsWith('temp-') ? step.step_id : null,
          step_order: step.step_order,
          step_type: step.step_type,
          label: step.label,
          description: step.description || null,
          is_required: step.is_required !== false,
          validation_rules: step.validation_rules || null,
          options: step.options || null,
          ai_model: step.ai_model || null,
        })),
      };

      const result = await updateProcedure(procedure.procedure_id, updateData);

      if (result.success) {
        showSuccess(t('procedures:success.procedure_updated'));
        navigate(`/procedures/${procedure.procedure_id}`);
      } else {
        showError(result.error || t('procedures:errors.update_failed'));
      }
    } catch (error) {
      showError(t('procedures:errors.unexpected_error'));
    }
  }, [validateForm, procedure, formData, steps, updateProcedure, showSuccess, showError, navigate, t]);

  const handleCancel = useCallback(() => {
    navigateBack(procedure ? `/procedures/${procedure.procedure_id}` : '/procedures');
  }, [navigateBack, procedure]);

  // Loading state
  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // Not found or system procedure
  if (!procedure) {
    return (
      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={() => navigate('/procedures')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight="bold">
            {t('procedures:messages.procedure_not_found')}
          </Typography>
        </Box>
        <Alert severity="error">
          {t('procedures:messages.procedure_not_found_description')}
        </Alert>
      </Box>
    );
  }

  return (
    <FormPage
      title={t('procedures:edit_procedure')}
      subtitle={t('procedures:form.edit_title', { name: procedure.name })}
      crumb={procedure.name}
      onBack={handleCancel}
      onSubmit={handleSubmit}
      fieldErrors={validationErrors}
      error={error}
      onErrorClose={clearError}
      maxWidth={1000}
    >
      <FormSection title={t('procedures:form.edit_procedure_information')}>
        <TextField
          fullWidth
          label={t('procedures:labels.name')}
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          error={!!validationErrors.name}
          helperText={validationErrors.name || t('procedures:form.name_helper')}
          required
          disabled={updating}
          placeholder={t('procedures:placeholders.name')}
          inputProps={{ maxLength: 255 }}
        />

        <TextField
          fullWidth
          multiline
          rows={3}
          label={t('procedures:labels.description')}
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          error={!!validationErrors.description}
          helperText={validationErrors.description || t('procedures:form.description_helper')}
          disabled={updating}
          placeholder={t('procedures:placeholders.description')}
          inputProps={{ maxLength: 2000 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={formData.is_active}
              onChange={(e) => handleInputChange('is_active', e.target.checked)}
              disabled={updating}
            />
          }
          label={t('procedures:labels.active')}
        />
      </FormSection>

      <FormSection>
        <ProcedureStepsEditor
          steps={steps}
          onStepsChange={setSteps}
          expandedStepId={expandedStepId}
          onExpandedStepIdChange={setExpandedStepId}
          disabled={updating}
        />
      </FormSection>

      <FormActions
        onCancel={handleCancel}
        submitType="submit"
        submitLabel={updating ? t('procedures:actions.updating') : t('procedures:actions.update_procedure')}
        submitIcon={<SaveIcon />}
        loading={updating}
      />
    </FormPage>
  );
}
